import { api } from '../api.js';
import { getState, subscribe } from '../state.js';
import { navigate } from '../router.js';
import { imageUrl } from '../config.js';
import { renderConceptCard } from '../components/conceptCard.js';
import { createGraphRenderer, GraphRenderer } from '../components/graphRenderer.js';
import { createModal } from '../components/modal.js';
import { getTranslatedFields, translate } from '../lib/i18n.js';
import { formatDateValue } from '../lib/dateFormat.js';
import type { ApiConceptDetail, ApiConceptListItem, ApiAdjacentConcept } from '../../shared/types.js';

export async function renderConceptView(container: HTMLElement, slug: string): Promise<void | (() => void)> {
  container.innerHTML = `<div class="loading">${translate('concept.loading', 'loading concept...')}</div>`;

  let langUnsub: (() => void) | null = null;

  try {
    const concept = await api.getConcept(slug) as ApiConceptDetail;
    let renderer: GraphRenderer | null = null;

    function renderContent(): void {
      container.innerHTML = '';

      const { title, summary, content } = getTranslatedFields(concept);
      const { lang } = getState();

      const view = document.createElement('div');
      view.className = 'view';

      const layout = document.createElement('div');
      layout.className = 'concept-view';

      // Main content
      const main = document.createElement('div');
      main.className = 'concept-main';

      // Build "also known as" block from aliases + translations
      const akaItems: string[] = [];
      // Add concept aliases (always shown regardless of language)
      if (concept.aliases && concept.aliases.length > 0) {
        for (const alias of concept.aliases) {
          akaItems.push(`<span class="concept-translations__item">${alias}</span>`);
        }
      }
      // Add translation names
      if (lang && concept.translations && concept.translations.length > 0) {
        // Viewing translated — show base title
        akaItems.push(`<span class="concept-translations__item" title="en">${concept.title}</span>`);
      } else if (concept.translations && concept.translations.length > 0) {
        // Viewing base — show translated titles
        for (const tr of concept.translations) {
          akaItems.push(`<span class="concept-translations__item" title="${tr.lang}">${tr.title}</span>`);
        }
      }
      const aliasHtml = akaItems.length > 0
        ? `<div class="concept-translations">
            <span class="concept-translations__label">${translate('concept.also_known_as', 'also known as')}</span>
            ${akaItems.join('')}
          </div>`
        : '';

      const formattedDate = formatDateValue(concept.date_value);
      const dateHtml = formattedDate
        ? `<div class="concept-main__date" style="font-family: var(--font-mono); font-size: 0.8rem; color: var(--dim); margin-bottom: 0.5rem;">${formattedDate}</div>`
        : '';

      const imageHtml = concept.image
        ? `<img class="concept-main__image" src="${imageUrl(concept.image)}" alt="${title}">`
        : '';

      main.innerHTML = `
        <h1 class="concept-main__title">${title}</h1>
        ${aliasHtml}
        ${dateHtml}
        ${summary ? `<p class="concept-main__summary">${summary}</p>` : ''}
        ${imageHtml}
        ${content ? `<div class="concept-main__content">${content}</div>` : ''}
      `;

      // Actions
      const actions = document.createElement('div');
      actions.className = 'concept-main__actions';

      const { user } = getState();

      // Admin delete button and date editor
      if (user && user.is_admin) {
        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'btn btn--danger btn--small';
        deleteBtn.textContent = translate('concept.delete', 'Delete concept');
        deleteBtn.addEventListener('click', async () => {
          if (!confirm(`Delete "${concept.title}"? This will remove all crosslinks for this concept. This cannot be undone.`)) return;
          try {
            await api.deleteConcept(slug);
            navigate('/graph');
          } catch (err) {
            alert('Failed to delete: ' + (err as Error).message);
          }
        });
        actions.appendChild(deleteBtn);

        // Inline date editor
        const dateEditor = document.createElement('div');
        dateEditor.style.cssText = 'display: flex; align-items: center; gap: 0.5rem; margin-top: 0.5rem;';
        dateEditor.innerHTML = `
          <label style="font-size: 0.8rem; color: var(--dim); font-family: var(--font-mono);">date:</label>
          <input class="input" type="text" id="admin-date-input" value="${concept.date_value || ''}" placeholder="e.g. 1993" style="width: 120px; font-size: 0.8rem; padding: 0.25rem 0.5rem;">
          <button class="btn btn--ghost btn--small" id="admin-date-save" style="font-size: 0.75rem; padding: 0.2rem 0.5rem;">save</button>
          <span id="admin-date-feedback" style="font-size: 0.75rem;"></span>
        `;
        actions.appendChild(dateEditor);

        (dateEditor.querySelector('#admin-date-save') as HTMLButtonElement).addEventListener('click', async () => {
          const val = (dateEditor.querySelector('#admin-date-input') as HTMLInputElement).value.trim() || null;
          const fb = dateEditor.querySelector('#admin-date-feedback') as HTMLElement;
          try {
            await api.updateConcept(slug, { date_value: val });
            concept.date_value = val;
            fb.textContent = 'saved';
            fb.style.color = 'var(--acid)';
            // Update displayed date
            const dateEl = main.querySelector('.concept-main__date') as HTMLElement | null;
            const newFormatted = formatDateValue(val);
            if (dateEl) {
              dateEl.textContent = newFormatted;
              dateEl.style.display = newFormatted ? '' : 'none';
            } else if (newFormatted) {
              const newDateEl = document.createElement('div');
              newDateEl.className = 'concept-main__date';
              newDateEl.style.cssText = 'font-family: var(--font-mono); font-size: 0.8rem; color: var(--dim); margin-bottom: 0.5rem;';
              newDateEl.textContent = newFormatted;
              const titleEl = main.querySelector('.concept-main__title') as HTMLElement;
              titleEl.insertAdjacentElement('afterend', newDateEl);
            }
          } catch (err) {
            fb.textContent = (err as Error).message;
            fb.style.color = 'var(--signal)';
          }
        });
      }

      main.appendChild(actions);

      // Flow link (async — added when ready)
      const flowLinkContainer = document.createElement('div');
      main.appendChild(flowLinkContainer);
      (api.hasFlow(slug) as Promise<{ hasFlow: boolean }>).then(({ hasFlow }) => {
        if (hasFlow) {
          const flowLink = document.createElement('a');
          flowLink.className = 'btn btn--ghost btn--small concept-flow-link';
          flowLink.innerHTML = `${translate('flow.view_flow', 'View procedure flow')} &rarr;`;
          flowLink.href = '#';
          flowLink.addEventListener('click', (e: Event) => {
            e.preventDefault();
            navigate(`/flow/${slug}`);
          });
          flowLinkContainer.appendChild(flowLink);
        }
      }).catch(() => { /* no flow data, no link */ });

      // Mini graph
      const graphContainer = document.createElement('div');
      graphContainer.className = 'concept-graph-container';
      main.appendChild(graphContainer);

      layout.appendChild(main);

      // Sidebar — adjacent concepts
      const sidebar = document.createElement('div');
      sidebar.className = 'concept-sidebar';

      sidebar.innerHTML = `<h3 class="concept-sidebar__title">${translate('concept.connected', 'connected concepts')} (${concept.adjacent.length})</h3>`;

      const list = document.createElement('div');
      list.className = 'concept-sidebar__list';

      for (const adj of concept.adjacent) {
        renderConceptCard(list, adj);
      }

      sidebar.appendChild(list);

      layout.appendChild(sidebar);

      view.appendChild(layout);
      container.appendChild(view);

      // Render mini graph
      const graphData = {
        nodes: [
          { slug: concept.slug, title, focal: true },
          ...concept.adjacent.map((a: ApiAdjacentConcept) => {
            const at = getTranslatedFields(a);
            return { slug: a.slug, title: at.title };
          }),
        ],
        edges: concept.adjacent.map((a: ApiAdjacentConcept) => ({ source: concept.slug, target: a.slug })),
      };

      if (renderer) renderer.dispose();
      renderer = null;

      if (graphData.nodes.length > 1) {
        renderer = createGraphRenderer(graphContainer, graphData, {
          cameraZ: 35,
          onNodeClick: (s: string) => navigate(`/concept/${s}`),
        });

        // Activate the flat focused layout for the focal node
        setTimeout(() => {
          if (renderer && renderer.focusNodeFlat) {
            renderer.focusNodeFlat(concept.slug, 1200);
          }
        }, 100);
      } else {
        graphContainer.innerHTML = `<div class="loading">${translate('concept.no_connections', 'no connections yet')}</div>`;
      }
    }

    renderContent();

    // Re-render when language changes
    langUnsub = subscribe('lang', () => renderContent());

    return () => {
      if (langUnsub) langUnsub();
      if (renderer) renderer.dispose();
    };
  } catch (err) {
    container.innerHTML = `<div class="view"><p class="form-error">${translate('concept.error', 'Failed to load concept: ')}${(err as Error).message}</p></div>`;
    return () => {
      if (langUnsub) langUnsub();
    };
  }
}
